import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowLeft, Heart, ListPlus, Clock, MapPin, Globe, Phone, CalendarCheck, Navigation, Quote } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { localized } from "@/i18n";
import { CATEGORIES, TAGS, BEST_MOMENTS, priceSymbol } from "@/constants";
import TrustBadges from "@/components/TrustBadges";
import AddToListDialog from "@/components/AddToListDialog";
import { ExperienceFeed } from "@/components/experiences/ExperienceFeed";
import { AudioGuide } from "@/components/assistant/AudioGuide";

const DAYS = { mon: { fr: "Lun", en: "Mon", es: "Lun" }, tue: { fr: "Mar", en: "Tue", es: "Mar" }, wed: { fr: "Mer", en: "Wed", es: "Mié" }, thu: { fr: "Jeu", en: "Thu", es: "Jue" }, fri: { fr: "Ven", en: "Fri", es: "Vie" }, sat: { fr: "Sam", en: "Sat", es: "Sáb" }, sun: { fr: "Dim", en: "Sun", es: "Dom" } };
const ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function PlaceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang, isFavorite, toggleFavorite } = useApp();
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);
  const [listOpen, setListOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/places/${id}`).then((r) => setPlace(r.data)).catch(() => setPlace(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="py-20 text-center text-ink-muted">{t("loading")}</div>;
  if (!place) return <div className="py-20 text-center text-ink-muted">{t("place_not_found")}</div>;

  const cat = CATEGORIES.find((c) => c.id === place.category);
  const CatIcon = cat ? Icons[cat.icon] : Icons.MapPin;
  const fav = isFavorite(place.id);
  const hasHours = place.hours && Object.keys(place.hours).length > 0;

  return (
    <div className="space-y-5 pb-6">
      {/* Gallery */}
      <div className="relative -mx-4 -mt-4">
        <div className="relative aspect-[4/3] bg-sand overflow-hidden">
          {place.images?.length ? (
            <img src={place.images[imgIdx]} alt={place.name} className="h-full w-full object-cover" />
          ) : (
            <div className="grid h-full w-full place-items-center bg-gradient-to-br from-terracotta-light to-sand">
              <CatIcon className="h-16 w-16 text-terracotta/40" />
            </div>
          )}
          <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
            <button data-testid="detail-back" onClick={() => navigate(-1)} className="grid h-10 w-10 place-items-center rounded-full bg-white/90 text-ink shadow backdrop-blur">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex gap-2">
              <button data-testid="detail-add-list" onClick={() => setListOpen(true)} className="grid h-10 w-10 place-items-center rounded-full bg-white/90 text-ink shadow backdrop-blur">
                <ListPlus className="h-5 w-5" />
              </button>
              <button data-testid="detail-fav" onClick={() => toggleFavorite(place.id)} className="grid h-10 w-10 place-items-center rounded-full bg-white/90 shadow backdrop-blur">
                <Heart className={`h-5 w-5 ${fav ? "fill-terracotta text-terracotta" : "text-ink"}`} />
              </button>
            </div>
          </div>
        </div>
        {place.images?.length > 1 && (
          <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide">
            {place.images.map((im, i) => (
              <button key={i} onClick={() => setImgIdx(i)} className={`h-14 w-20 shrink-0 overflow-hidden rounded-lg border-2 ${i === imgIdx ? "border-terracotta" : "border-transparent"}`}>
                <img src={im} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white" style={{ backgroundColor: cat?.color }}>
            <CatIcon className="h-3 w-3" /> {localized(cat?.label, lang)}
          </span>
          {place.status === "temporarily_closed" ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-red-300 px-2.5 py-1 text-[11px] font-semibold text-red-600">{t("closed_temp")}</span>
          ) : place.open_now === true ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600"><span className="h-2 w-2 rounded-full bg-emerald-500" />{t("open")}</span>
          ) : place.open_now === false ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-500"><span className="h-2 w-2 rounded-full bg-red-400" />{t("closed")}</span>
          ) : null}
        </div>
        <h1 className="mt-2 font-heading text-2xl font-extrabold text-ink">{place.name}</h1>
        <div className="mt-1.5 flex items-center gap-3 text-sm text-ink-muted">
          <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{place.neighborhood}</span>
          <span className="font-mono font-bold text-emerald-700">{place.price_level ? priceSymbol(place.price_level) : t("no_budget")}</span>
          {place.duration_min && <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" />{place.duration_min} {t("min")}</span>}
        </div>
      </div>

      <p className="text-sm leading-relaxed text-ink/80">{localized(place.summary, lang)}</p>

      <TrustBadges place={place} lang={lang} />

      {/* Why go */}
      {localized(place.why_go, lang) && (
        <div className="relative rounded-2xl border-l-4 border-terracotta bg-terracotta-light p-4">
          <Quote className="absolute right-3 top-3 h-5 w-5 text-terracotta/40" />
          <p className="text-xs font-bold uppercase tracking-wide text-terracotta">{t("why_go")}</p>
          <p className="mt-1.5 text-sm font-medium leading-relaxed text-ink">{localized(place.why_go, lang)}</p>
        </div>
      )}

      {/* Tags */}
      {place.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {place.tags.map((tag) => (
            <span key={tag} className="rounded-full bg-sand px-3 py-1 text-xs font-medium text-ink/70">{localized(TAGS[tag], lang) || tag}</span>
          ))}
        </div>
      )}

      {/* Info list */}
      <div className="space-y-2.5 rounded-2xl border border-border bg-white p-4">
        <div className="flex items-start gap-3">
          <Clock className="mt-0.5 h-4.5 w-4.5 text-sea" />
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-wide text-ink-muted">{t("hours")}</p>
            {hasHours ? (
              <div className="mt-1 space-y-0.5">
                {ORDER.filter((d) => place.hours[d]).map((d) => (
                  <div key={d} className="flex justify-between text-sm text-ink">
                    <span className="font-medium">{localized(DAYS[d], lang)}</span>
                    <span className="text-ink-muted">{place.hours[d].map((s) => s.join("–")).join(", ")}</span>
                  </div>
                ))}
              </div>
            ) : <p className="mt-0.5 text-sm text-ink-muted">{t("no_hours")}</p>}
          </div>
        </div>
        {place.address && (
          <div className="flex items-center gap-3 border-t border-border pt-2.5">
            <MapPin className="h-4.5 w-4.5 text-sea" />
            <span className="text-sm text-ink">{place.address}</span>
          </div>
        )}
        {place.phone && (
          <a href={`tel:${place.phone}`} className="flex items-center gap-3 border-t border-border pt-2.5 text-sm text-ink hover:text-terracotta">
            <Phone className="h-4.5 w-4.5 text-sea" /> {place.phone}
          </a>
        )}
        {place.website && (
          <a data-testid="detail-website" href={place.website} target="_blank" rel="noreferrer" className="flex items-center gap-3 border-t border-border pt-2.5 text-sm text-ink hover:text-terracotta">
            <Globe className="h-4.5 w-4.5 text-sea" /> {t("website")}
          </a>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <a
          data-testid="detail-route"
          href={`https://www.google.com/maps/dir/?api=1&destination=${place.lat},${place.lng}`}
          target="_blank" rel="noreferrer"
          className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-sea py-3.5 text-sm font-bold text-white hover:bg-sea-hover"
        >
          <Navigation className="h-4.5 w-4.5" /> {t("route")}
        </a>
        {place.booking_url && (
          <a href={place.booking_url} target="_blank" rel="noreferrer" className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-terracotta py-3.5 text-sm font-bold text-white hover:bg-terracotta-hover">
            <CalendarCheck className="h-4.5 w-4.5" /> {t("book")}
          </a>
        )}
      </div>

      <AddToListDialog open={listOpen} onOpenChange={setListOpen} placeId={place.id} />
      <ExperienceFeed key={place.id} place={place} />
      <AudioGuide key={`audio-${place.id}`} placeId={place.id} />
    </div>
  );
}
